"use client";

import clsx from "clsx";
import Link from "next/link";
import { useEffect, useState } from "react";
import { themeImage } from "@/lib/media";
import { useModals } from "@/components/modals/ModalProvider";
import PageNav from "./PageNav";

const MENU_LINKS = [
  { url: "/become-a-das-centre", label: "Join us" },
  { url: "/about-us", label: "About DAS" },
  { url: "/contact", label: "Contact us" },
];

/*
 * Over a hero the header sits on the artwork rather than a bar of its own. That
 * is SiteWrapper's transparent-header mode while the page is still on the hero
 * and the menu is shut; the classes below that carry the long
 * group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:
 * prefix are that state. Scrolling off the hero or opening the menu brings the
 * painted bar back.
 */

const LOGO_IMG =
  "block h-auto w-full transition-opacity duration-400 ease-[ease]";

const MENU_ITEM = "m-0 not-first:mt-1";

const MENU_LINK =
  "block border-b border-das-hairline py-4 font-das text-[28px] leading-[1.2] font-bold tracking-[-0.02em] text-das-black no-underline transition-opacity duration-400 ease-[ease] hover:text-das-black hover:opacity-60 mobile:text-[24px]";

const BURGER_LINE =
  "block h-0.5 w-full rounded-[2px] bg-das-black transition-[transform,opacity,background-color] duration-400 ease-[ease] group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:bg-white";

type Props = {
  /** Inner pages carry the secondary page nav as a second row of the header. */
  hasPageNav: boolean;
};

/**
 * Minimal header, v2: a fixed bar with the logo, a login pill and a burger that
 * opens a full-screen menu living inside the header.
 *
 * The header publishes --header-height and --header-sticky-height: 56px, or
 * 56px plus the 44px page nav on inner pages. They are declared on the element
 * itself, since the sticky scripts read them off #site-header, and page-wide by
 * globals.css off data-page-nav, which is what .main and the menu clear.
 *
 * The blur lives on the bar rather than on the header: backdrop-filter makes an
 * element a containing block for fixed descendants, which would trap the
 * full-screen menu inside the 56px bar.
 */
export default function Header({ hasPageNav }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { openModal } = useModals();

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const login = (event: React.MouseEvent) => {
    event.preventDefault();
    setMenuOpen(false);
    openModal("signin-modal");
  };

  return (
    <header
      id="site-header"
      data-page-nav={hasPageNav}
      data-menu-open={menuOpen}
      className={clsx(
        "group/header fixed top-0 right-0 left-0 z-9999",
        hasPageNav
          ? "[--header-height:100px] [--header-sticky-height:100px]"
          : "[--header-height:56px] [--header-sticky-height:56px]",
      )}
    >
      {/* The bar drops its translucency while the menu is open, or the white sheet behind washes through as a seam. */}
      <div
        className={clsx(
          "relative z-2 h-14 border-b border-das-hairline bg-white/80 backdrop-blur-[20px] transition-[background-color,border-color] duration-400 ease-[ease]",
          "group-data-[menu-open=true]/header:bg-white group-data-[menu-open=true]/header:backdrop-filter-none",
          "group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:border-b-transparent group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:bg-transparent group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:backdrop-filter-none",
        )}
      >
        <div className="shell flex h-full shell-s flex-wrap items-center">
          <div className="w-[130px] mobile:w-[110px]">
            <Link
              href="/"
              className="relative block h-auto w-full indent-[-4004px] align-middle text-[0px] leading-[0] no-underline"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={clsx(
                  LOGO_IMG,
                  "group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:opacity-0",
                )}
                src={themeImage("logo-v2-dark.svg")}
                alt="DAS Academies"
              />

              {/* Swapped in over a hero, where the dark mark would disappear into the artwork. Stacked on the dark one so nothing shifts as they cross-fade. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={clsx(
                  LOGO_IMG,
                  "absolute top-0 left-0 opacity-0 group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:opacity-100",
                )}
                src={themeImage("logo-v2.svg")}
                alt=""
              />
            </Link>
          </div>

          <div className="flex w-[calc(100%-130px)] items-center justify-end pl-5 mobile:w-[calc(100%-110px)] mobile:pl-[15px]">
            <div className="block">
              {/* Over a hero the green pill becomes a frosted chip of the hero itself. */}
              <a
                href="#"
                className="inline-block rounded-[100px] bg-das-green px-4.5 py-2 text-center font-das text-[14px] leading-[1.2] font-medium text-white no-underline transition-[background-color,backdrop-filter,opacity] duration-400 ease-[ease] hover:text-white hover:opacity-80 mobile:px-[15px] mobile:py-[7px] mobile:text-[13px] group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:bg-white/18 group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:group-data-[menu-open=false]/header:backdrop-blur-[8px]"
                onClick={login}
              >
                Login
              </a>
            </div>

            <div className="ml-5 block mobile:ml-[15px]">
              <button
                type="button"
                className="block h-4 w-5.5 cursor-pointer border-0 bg-transparent outline-none"
                aria-label="Menu"
                aria-controls="site-menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span
                  className={clsx(
                    BURGER_LINE,
                    "group-data-[menu-open=true]/header:[transform:translateY(7px)_rotate(45deg)]",
                  )}
                ></span>
                <span
                  className={clsx(
                    BURGER_LINE,
                    "mt-[5px] group-data-[menu-open=true]/header:opacity-0",
                  )}
                ></span>
                <span
                  className={clsx(
                    BURGER_LINE,
                    "mt-[5px] group-data-[menu-open=true]/header:[transform:translateY(-7px)_rotate(-45deg)]",
                  )}
                ></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {hasPageNav && <PageNav />}

      {/* Sits below the bar rather than over it, so the burger stays reachable and reads as the close control. */}
      <div
        id="site-menu"
        className="invisible fixed inset-0 z-1 overflow-y-auto bg-white pt-[calc(var(--header-height)+40px)] opacity-0 transition-[opacity,visibility] duration-400 ease-[ease] group-data-[menu-open=true]/header:visible group-data-[menu-open=true]/header:opacity-100"
        aria-hidden={!menuOpen}
      >
        <div className="shell shell-s">
          <nav aria-label="Main">
            <ul className="m-0 list-none p-0">
              {MENU_LINKS.map((link) => (
                <li key={link.url} className={MENU_ITEM}>
                  <Link
                    href={link.url}
                    className={MENU_LINK}
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}

              <li className={MENU_ITEM}>
                <a href="#" className={MENU_LINK} onClick={login}>
                  Login
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}
